import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin, ExternalLink, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";

export default function Footer() {
  const { language } = useLanguage();
  
  return (
    <footer className="bg-card border-t border-border">
      {/* Wave divider */}
      <div className="w-full overflow-hidden leading-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px] text-background">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor"></path>
        </svg>
      </div>
      
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
          {/* Column 1: Logo and Description */}
          <div className="flex flex-col lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-5">
              <span className="text-xl font-heading font-bold logo-gradient">1Group</span>
            </Link>
            <p className="text-muted-foreground text-sm mb-6 max-w-md">
              {getTranslation("footerSupportCenter", language)}
            </p>
            
            <div className="flex space-x-3">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noreferrer" 
                className="bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary/80 h-10 w-10 rounded-lg flex items-center justify-center transition-colors"
              >
                <Instagram className="h-5 w-5" />
                <span className="sr-only">Instagram</span>
              </a>
            </div>
          </div>
          
          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-5">{getTranslation("footerServices", language)}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/kompleksni-poslugy" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  Комплексні послуги
                </Link>
              </li>
              <li>
                <Link to="/uslugi#company" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerCompanyServices", language)}
                </Link>
              </li>
              <li>
                <Link to="/uslugi#auto" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerVehicleRegistration", language)}
                </Link>
              </li>
              <li>
                <Link to="/uslugi#support" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerResidencePermit", language)}
                </Link>
              </li>
              <li>
                <Link to="/uslugi#tax" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerAccounting", language)}
                </Link>
              </li>
              <li>
                <Link to="/cennik" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerPricing", language)}
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Column 3: Company */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-5">{getTranslation("footerCompany", language)}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerFaq", language)}
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerContact", language)}
                </Link>
              </li>
              <li>
                <Link to="/polityka-prywatnosci" className="text-sm text-muted-foreground hover:text-primary transition-colors hover-underline-dot inline-block">
                  {getTranslation("footerPrivacyPolicy", language)}
                </Link>
              </li>
            </ul>
            
            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                {getTranslation("footerOpeningHours", language)}
              </h4>
              <p className="text-sm text-muted-foreground">
                {getTranslation("footerWorkingDays", language)}
              </p>
              <p className="text-sm text-muted-foreground">
                {getTranslation("footerWeekends", language)}
              </p>
            </div>
          </div>
          
          {/* Column 4: Contact */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-5">{getTranslation("footerContact", language)}</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-sm block">ul. Stary Rynek 61/2</span>
                  <span className="text-sm text-muted-foreground">61-772 Poznań</span>
                </div>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                <a href="mailto:kontakt1group.pl@gmail.com" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  kontakt1group.pl@gmail.com
                </a>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                <a href="tel:+48886695593" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  +48 886 695 593
                </a>
              </li>
            </ul>
            
            <Button asChild variant="outline" size="sm" className="mt-6 w-full rounded-lg border-primary/20 hover:bg-primary/10 hover:text-primary hover:border-primary/30">
              <Link to="/kontakt" className="flex items-center justify-center">
                {getTranslation("footerRouteMap", language)}
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Bottom Footer */}
      <div className="border-t border-border py-6">
        <div className="container flex flex-col sm:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground mb-4 sm:mb-0">
            {getTranslation("footerCopyright", language)}
          </p>
          <div className="flex space-x-6">
            <Link to="/polityka-prywatnosci" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              {getTranslation("footerPrivacyPolicy", language)}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}